import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  selectAccountResendVerificationError,
  selectAccountVerificationError,
  selectPostingAccountVerification,
  selectPostingResendAccountVerification,
} from 'src/app/AccountOverview/redux/accountOverview.selectors'
import {
  selectCreditCardIdFromRoute,
  selectPatchingCreditCardFromState,
  selectPutCreditCardAccountCausedErrorFromState,
  selectPutCreditCardBillingCausedErrorFromState,
  selectPutCreditCardErrorDetailMapFromState,
  selectPutCreditCardTopLevelErrorFromState,
  selectPuttingCreditCardAccountFromState,
  selectPuttingCreditCardBillingFromState,
  selectPuttingCreditCardFromState,
  selectReplaceCreditCardErrorDetailMapFromState,
  selectReplaceCreditCardTopLevelErrorFromState,
} from 'src/app/AccountSettings/redux/accountSettings.selectors'
import AccountBillingInformationBox, {
  AccountBillingInformationCountryFields,
  AccountBillingInformationFlatFields,
} from 'src/components/AccountBillingInformationBox/AccountBillingInformationBox'
import AccountInformationBox, {
  AccountInformationFlatFields,
} from 'src/components/AccountInformationBox/AccountInformationBox'
import AccountVerificationRequiredBox from 'src/components/AccountVerificationRequiredBox/AccountVerificationRequiredBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import CardInformationBox, {
  CardInformationBoxFlatFields,
} from 'src/components/CardInformationBox/CardInformationBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import TestId from 'src/components/TestId/TestId'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { SupportedBrands } from 'src/enums'
import PhysicalCardTransactionsPanel from 'src/feature/PhysicalCardTransactionsPanel/PhysicalCardTransactionsPanel'
import VirtualCardCapabilitiesPanel from 'src/feature/VirtualCardCapabilitiesPanel/VirtualCardCapabilitiesPanel'
import { useBackgroundDispatch, usePrevious } from 'src/hooks'
import { PhysicalCardStatus } from 'src/models/creditCard.models'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { getCountrys } from 'src/redux/actions/data/countrys.actions'
import {
  postCreditCardStartVerificationThenOpenToast,
  postCreditCardVerifyThenOpenToast,
  PUT_CREDIT_CARD_ACCOUNT_INIT,
  PUT_CREDIT_CARD_BILLING_INIT,
  putCreditCard,
  replaceCreditCard,
} from 'src/redux/actions/data/creditCards.actions'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

const AccountSettings: React.FC = () => {
  const creditCardId = useSelector(selectCreditCardIdFromRoute)
  const { data: creditCard, refetch: refetchCreditCard } = useQueryCreditCard({
    creditCardId: creditCardId,
    enabled: !!creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.id,
    enabled: !!creditCard?.id,
    retry: false,
  })

  const { whiteLabelIncludes } = useWhitelabel()

  const isExtendBrand = whiteLabelIncludes(SupportedBrands.Extend)

  const puttingCreditCard = useSelector(selectPuttingCreditCardFromState)
  const puttingCreditCardAccount = useSelector(
    selectPuttingCreditCardAccountFromState,
  )
  const puttingCreditCardBilling = useSelector(
    selectPuttingCreditCardBillingFromState,
  )
  const putCreditCardAccountCausedError = useSelector(
    selectPutCreditCardAccountCausedErrorFromState,
  )
  const putCreditCardBillingCausedError = useSelector(
    selectPutCreditCardBillingCausedErrorFromState,
  )
  const patchingCreditCard = useSelector(selectPatchingCreditCardFromState)
  const putCreditCardTopLevelError = useSelector(
    selectPutCreditCardTopLevelErrorFromState,
  )
  const putCreditCardErrorDetailMap = useSelector(
    selectPutCreditCardErrorDetailMapFromState,
  )
  const replaceCreditCardTopLevelError = useSelector(
    selectReplaceCreditCardTopLevelErrorFromState,
  )
  const replaceCreditCardErrorDetailMap = useSelector(
    selectReplaceCreditCardErrorDetailMapFromState,
  )
  const postingAccountVerification = useSelector(
    selectPostingAccountVerification,
  )
  const accountVerificationError = useSelector(selectAccountVerificationError)
  const postingResendAccountVerification = useSelector(
    selectPostingResendAccountVerification,
  )
  const accountResendVerificationError = useSelector(
    selectAccountResendVerificationError,
  )

  const canUpdateCreditCard = !!creditCardPermissions?.canUpdateCreditCard

  const dispatch = useBackgroundDispatch()

  const prevPuttingAccount = usePrevious(puttingCreditCardAccount)
  const prevPuttingBilling = usePrevious(puttingCreditCardBilling)
  const prevPuttingAny = usePrevious(puttingCreditCard)

  const refreshCard = useCallback(() => {
    refetchCreditCard()
  }, [creditCardId /*, refetch */])

  useEffect(() => {
    const finished = prevPuttingAccount && !puttingCreditCardAccount
    const succeeded = finished && !putCreditCardAccountCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingAccount,
    puttingCreditCardAccount,
    putCreditCardAccountCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingBilling && !puttingCreditCardBilling
    const succeeded = finished && !putCreditCardBillingCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingBilling,
    puttingCreditCardBilling,
    putCreditCardBillingCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingAny && !puttingCreditCard
    const noTopLevelError =
      !replaceCreditCardTopLevelError &&
      Object.keys(replaceCreditCardErrorDetailMap ?? {}).length === 0

    if (finished && noTopLevelError) {
      refreshCard()
    }
  }, [
    prevPuttingAny,
    puttingCreditCard,
    replaceCreditCardTopLevelError,
    replaceCreditCardErrorDetailMap,
    refreshCard,
  ])

  useEffect((): void => {
    dispatch(getCountrys())
  }, [])

  const onBillingInformationSuccessfulSubmit = (
    flatFields: AccountBillingInformationFlatFields,
    countryFields: AccountBillingInformationCountryFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...countryFields,
          },
          false,
          {
            type: PUT_CREDIT_CARD_BILLING_INIT,
          },
        ),
      )
    }
  }

  const onAccountInformationSuccessfulSubmit = (
    flatFields: AccountInformationFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...creditCard.address,
          },
          false,
          {
            type: PUT_CREDIT_CARD_ACCOUNT_INIT,
          },
        ),
      )
    }
  }

  const onCardInformationSuccessfulSubmit = (
    flatFields: CardInformationBoxFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        replaceCreditCard(creditCard.id, {
          ...flatFields,
        }),
      )
    }
  }

  const handleVerifyCreditCard = useCallback(
    (code: string): void => {
      dispatch(postCreditCardVerifyThenOpenToast(creditCardId, { code }))
    },
    [dispatch, creditCardId],
  )

  const handleResendVerification = useCallback((): void => {
    dispatch(postCreditCardStartVerificationThenOpenToast(creditCardId))
  }, [dispatch, creditCardId])

  const showVerificationRequired =
    creditCard?.physicalCard.status === PhysicalCardStatus.NeedsCVSVerification

  return creditCard !== undefined ? (
    <BackgroundPageContent>
      {showVerificationRequired ? (
        <AccountVerificationRequiredBox
          accountResendVerificationError={accountResendVerificationError}
          accountVerificationError={accountVerificationError}
          canVerifyAgain={creditCard.canVerifyAgain}
          handleResendVerification={handleResendVerification}
          handleVerifyCreditCard={handleVerifyCreditCard}
          postingAccountVerification={postingAccountVerification}
          postingResendAccountVerification={postingResendAccountVerification}
          timeUntilNextVerificationRequested={
            creditCard.timeUntilNextVerificationRequested
          }
          canUpdateCreditCard={canUpdateCreditCard}
        />
      ) : (
        <HorizontalContentCollection>
          <VerticalContentCollection>
            {!creditCard.partner?.isVirtualAccount && (
              <TestId testId={'cardInformationBox'}>
                <CardInformationBox
                  creditCard={creditCard}
                  puttingCreditCard={puttingCreditCard}
                  patchingCreditCard={patchingCreditCard}
                  topLevelError={replaceCreditCardTopLevelError}
                  errorDetailMap={replaceCreditCardErrorDetailMap}
                  onSuccessfulSubmit={onCardInformationSuccessfulSubmit}
                  hasEditPermissions={
                    creditCard.features.rcnReplacementEnabled &&
                    canUpdateCreditCard
                  }
                />
              </TestId>
            )}
            <AccountBillingInformationBox
              creditCard={creditCard}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardBilling={puttingCreditCardBilling}
              topLevelError={putCreditCardTopLevelError}
              errorDetailMap={putCreditCardErrorDetailMap}
              onSuccessfulSubmit={onBillingInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardBillingCausedError}
            />
          </VerticalContentCollection>
          <VerticalContentCollection>
            <AccountInformationBox
              creditCard={creditCard}
              topLevelError={putCreditCardTopLevelError}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardAccount={puttingCreditCardAccount}
              onSuccessfulSubmit={onAccountInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardAccountCausedError}
            />
            {/*
              Only show this for the extend brand. Used to be both extend brand
              and astrada enable flag
          */}
            {isExtendBrand && (
              <PhysicalCardTransactionsPanel creditCard={creditCard} />
            )}
            {isExtendBrand && (
              <VirtualCardCapabilitiesPanel creditCard={creditCard} />
            )}
          </VerticalContentCollection>
        </HorizontalContentCollection>
      )}
    </BackgroundPageContent>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default AccountSettings
